import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Card, CardContent } from './ui/card';
import {
  MessageSquare, CheckCircle, XCircle, Loader2, FileText, AlertCircle,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Inline panel that shows the promoter all PENDING corrections proposed
 * by the admin reviewer during ``preparacion_imprenta``. Approving a
 * correction replaces the chapter content and refreshes the page count;
 * rejecting it leaves the content untouched.
 *
 * Resolved (approved/rejected) corrections are surfaced in a
 * collapsible history block below the active list.
 *
 * The side-by-side diff dialog renders the original chapter body on the
 * left and the proposed body on the right with subtle background tints
 * so the promoter can compare paragraph-by-paragraph at a glance.
 *
 * Props:
 *   - bookId
 *   - onResolved(): called after a correction is approved/rejected so
 *     the parent page can refresh the book document.
 */
export const ProposedCorrectionsPanel = ({ bookId, onResolved }) => {
  const [data, setData] = useState({ corrections: [], pending_count: 0, total: 0 });
  const [loading, setLoading] = useState(false);
  const [diffOpen, setDiffOpen] = useState(false);
  const [activeCorrection, setActiveCorrection] = useState(null);
  const [rejectNote, setRejectNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  const refresh = useCallback(async () => {
    if (!bookId) return;
    setLoading(true);
    try {
      const res = await axios.get(
        `${API_URL}/api/books/${bookId}/proposed-corrections`,
        { withCredentials: true },
      );
      setData(res.data);
    } catch (err) {
      // Silent — the panel just stays empty if there's a problem.
      if (err.response?.status !== 403 && err.response?.status !== 404) {
        toast.error(err.response?.data?.detail || 'No se pudieron cargar las correcciones');
      }
    } finally {
      setLoading(false);
    }
  }, [bookId]);

  useEffect(() => { refresh(); }, [refresh]);

  const openDiff = (correction) => {
    setActiveCorrection(correction);
    setRejectNote('');
    setDiffOpen(true);
  };

  const approve = async () => {
    if (!activeCorrection) return;
    setBusy(true);
    try {
      await axios.post(
        `${API_URL}/api/books/${bookId}/proposed-corrections/${activeCorrection.correction_id}/approve`,
        {},
        { withCredentials: true },
      );
      toast.success(`Corrección aplicada a "${activeCorrection.chapter_heading}"`);
      setDiffOpen(false);
      setActiveCorrection(null);
      await refresh();
      onResolved?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo aprobar');
    } finally {
      setBusy(false);
    }
  };

  const reject = async () => {
    if (!activeCorrection) return;
    setBusy(true);
    try {
      await axios.post(
        `${API_URL}/api/books/${bookId}/proposed-corrections/${activeCorrection.correction_id}/reject`,
        { resolved_note: rejectNote.trim() },
        { withCredentials: true },
      );
      toast.success('Corrección rechazada — el texto original se mantiene');
      setDiffOpen(false);
      setActiveCorrection(null);
      setRejectNote('');
      await refresh();
      onResolved?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo rechazar');
    } finally {
      setBusy(false);
    }
  };

  const formatDate = (iso) => {
    if (!iso) return '—';
    try {
      const d = new Date(iso);
      return d.toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' }) +
        ' · ' + d.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
    } catch { return iso; }
  };

  // Don't render anything if the book has no proposed corrections at all.
  if (loading) {
    return (
      <div className="flex items-center gap-2 text-xs text-muted-foreground py-3">
        <Loader2 className="w-3 h-3 animate-spin" /> Cargando correcciones del revisor…
      </div>
    );
  }
  if (data.total === 0) return null;

  const pending = data.corrections.filter(c => c.status === 'pending');
  const resolved = data.corrections.filter(c => c.status !== 'pending');

  return (
    <Card className="card-elevated border-l-4 border-l-amber-500" data-testid="proposed-corrections-panel">
      <CardContent className="p-5">
        <div className="flex items-center gap-2 mb-3">
          <MessageSquare className="w-5 h-5 text-amber-600" />
          <h3 className="font-heading text-base font-semibold">
            Correcciones propuestas por el revisor
          </h3>
          {pending.length > 0 && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 font-medium">
              {pending.length} pendiente{pending.length === 1 ? '' : 's'}
            </span>
          )}
        </div>

        {pending.length === 0 ? (
          <p className="text-xs text-muted-foreground">
            No tienes correcciones pendientes — todas han sido resueltas.
          </p>
        ) : (
          <>
            <p className="text-xs text-muted-foreground mb-3">
              El revisor ha propuesto cambios en algunos capítulos. Compara cada propuesta con el texto actual y apruébala o recházala. El libro no se puede enviar a imprenta hasta que resuelvas todas las propuestas.
            </p>
            <div className="space-y-2">
              {pending.map((c) => (
                <button
                  key={c.correction_id}
                  onClick={() => openDiff(c)}
                  className="w-full text-left p-3 rounded-lg border border-amber-200 bg-amber-50/40 hover:bg-amber-50 transition-colors flex items-start gap-3"
                  data-testid={`pending-correction-${c.correction_id}`}
                >
                  <FileText className="w-4 h-4 text-amber-700 mt-0.5 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{c.chapter_heading}</p>
                    {c.note && (
                      <p className="text-xs text-muted-foreground italic mt-0.5 truncate">"{c.note}"</p>
                    )}
                    <p className="text-[11px] text-muted-foreground mt-0.5">
                      Propuesto por {c.proposed_by_name} · {formatDate(c.proposed_at)}
                    </p>
                  </div>
                  <span className="text-xs px-2 py-1 rounded-full bg-amber-200 text-amber-900 font-medium flex-shrink-0">
                    Revisar →
                  </span>
                </button>
              ))}
            </div>
          </>
        )}

        {resolved.length > 0 && (
          <div className="mt-4 pt-3 border-t">
            <button
              onClick={() => setShowHistory(v => !v)}
              className="text-xs text-muted-foreground hover:text-foreground transition-colors"
              data-testid="toggle-corrections-history"
            >
              {showHistory ? '▼' : '▶'} Historial de correcciones resueltas ({resolved.length})
            </button>
            {showHistory && (
              <div className="mt-2 space-y-1.5">
                {resolved.map((c) => (
                  <div
                    key={c.correction_id}
                    className="flex items-start gap-2 text-xs p-2 rounded bg-muted/30"
                    data-testid={`resolved-correction-${c.correction_id}`}
                  >
                    {c.status === 'approved' ? (
                      <CheckCircle className="w-3.5 h-3.5 text-emerald-600 mt-0.5 flex-shrink-0" />
                    ) : (
                      <XCircle className="w-3.5 h-3.5 text-gray-500 mt-0.5 flex-shrink-0" />
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{c.chapter_heading}</p>
                      <p className="text-[10px] text-muted-foreground">
                        {c.status === 'approved' ? 'Aprobada' : 'Rechazada'} el {formatDate(c.resolved_at)}
                        {c.resolved_note ? ` · "${c.resolved_note}"` : ''}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </CardContent>

      {/* Side-by-side diff dialog */}
      <Dialog open={diffOpen} onOpenChange={(o) => { if (!o) { setDiffOpen(false); setActiveCorrection(null); } }}>
        <DialogContent className="max-w-6xl max-h-[92vh] overflow-hidden flex flex-col p-0" data-testid="correction-diff-dialog">
          <DialogHeader className="px-5 pt-5 pb-3 border-b bg-muted/30">
            <DialogTitle className="flex items-center gap-2 text-lg">
              <MessageSquare className="w-5 h-5 text-amber-600" />
              Revisar propuesta de corrección
            </DialogTitle>
            <DialogDescription>
              {activeCorrection ? (
                <>
                  Capítulo <strong>{activeCorrection.chapter_heading}</strong> · Propuesto por {activeCorrection.proposed_by_name}
                  {activeCorrection.note && (
                    <span className="block mt-1 text-amber-700">📝 Nota del revisor: "{activeCorrection.note}"</span>
                  )}
                </>
              ) : null}
            </DialogDescription>
          </DialogHeader>

          {activeCorrection && (
            <div className="flex-1 overflow-hidden flex flex-col">
              <div className="grid grid-cols-2 gap-0 flex-1 min-h-0 overflow-hidden">
                {/* Original (left) */}
                <div className="border-r overflow-y-auto bg-rose-50/30">
                  <div className="sticky top-0 z-10 bg-rose-100/80 backdrop-blur px-4 py-2 border-b border-rose-200">
                    <p className="text-xs font-semibold text-rose-900 uppercase tracking-wide">Original (actual)</p>
                  </div>
                  <pre
                    className="p-4 text-sm whitespace-pre-wrap font-mono leading-relaxed text-gray-800"
                    data-testid="correction-original"
                  >{activeCorrection.original_body}</pre>
                </div>
                {/* Proposed (right) */}
                <div className="overflow-y-auto bg-emerald-50/30">
                  <div className="sticky top-0 z-10 bg-emerald-100/80 backdrop-blur px-4 py-2 border-b border-emerald-200">
                    <p className="text-xs font-semibold text-emerald-900 uppercase tracking-wide">Propuesta del revisor</p>
                  </div>
                  <pre
                    className="p-4 text-sm whitespace-pre-wrap font-mono leading-relaxed text-gray-800"
                    data-testid="correction-proposed"
                  >{activeCorrection.proposed_body}</pre>
                </div>
              </div>

              <div className="border-t bg-muted/20 px-5 py-3 flex-shrink-0">
                <label className="text-xs text-muted-foreground block mb-1">
                  Si vas a rechazar, opcionalmente explica por qué para que el revisor entienda:
                </label>
                <Textarea
                  value={rejectNote}
                  onChange={(e) => setRejectNote(e.target.value)}
                  placeholder="Ej: prefiero mantener el tono original, este pasaje refleja mejor la realidad…"
                  className="min-h-[60px] text-sm resize-none"
                  data-testid="reject-note-input"
                />
              </div>
            </div>
          )}

          <DialogFooter className="px-5 py-3 border-t bg-muted/30 gap-2 flex-shrink-0">
            <Button
              variant="outline"
              onClick={reject}
              disabled={busy}
              className="gap-2 border-rose-300 text-rose-700 hover:bg-rose-50"
              data-testid="reject-correction-btn"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <XCircle className="w-4 h-4" />}
              Rechazar — mantener original
            </Button>
            <Button
              onClick={approve}
              disabled={busy}
              className="gap-2 bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="approve-correction-btn"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
              Aprobar — aplicar la corrección
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Help banner when book is in preparacion_imprenta and no pending */}
      {data.book_status === 'preparacion_imprenta' && pending.length === 0 && resolved.length > 0 && (
        <div className="mx-5 mb-4 -mt-2 flex items-start gap-2 p-2.5 rounded-md bg-emerald-50 border border-emerald-200">
          <AlertCircle className="w-4 h-4 text-emerald-700 mt-0.5 flex-shrink-0" />
          <p className="text-xs text-emerald-900">
            Has resuelto todas las correcciones propuestas. El revisor ya puede aprobar el libro para imprenta.
          </p>
        </div>
      )}
    </Card>
  );
};

export default ProposedCorrectionsPanel;
